/**
 * Email Verification Modal
 *
 * Enforces email verification with escalating severity:
 *
 * 1. **Prompt (Dismissible)** — status=awaiting, token not yet expired.
 *    A gentle reminder the user can close and return to the app.
 *
 * 2. **Blocking (Non-dismissible)** — token expired AND sentCount < 3.
 *    User MUST resend a new verification email before continuing.
 *
 * 3. **Revoked** — sentCount >= 3 and still not verified.
 *    Account access is revoked. User is signed out.
 */

"use client";

import {
	useState,
	useCallback,
	useEffect,
	useId,
	useLayoutEffect,
	useMemo,
	useRef,
	type KeyboardEvent,
} from "react";
import { usePathname } from "next/navigation";
import { signOut } from "next-auth/react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/hooks/use-auth";
import { useSessionActivity } from "@/contexts/session-activity-context";
import { useModalFocusManagement } from "@/hooks/useModalFocusManagement";

// ============================================================================
// Strings (extracted for future i18n — see docs/I18N_CONVENTIONS.md)
// ============================================================================

const STRINGS = {
	prompt: {
		title: "Verify Your Email",
		description:
			"We sent a verification link to your email address. Please check your inbox and click the link to verify your account.",
		dismiss: "I'll do it later",
		resend: "Resend Email",
		checkSpam: "Don't see it? Check your spam folder.",
	},
	blocking: {
		title: "Verification Required",
		description:
			"Your verification link has expired. Please request a new one to continue using VastiPost.",
		resend: "Send New Link",
	},
	revoked: {
		title: "Account Access Revoked",
		description:
			"Your account has been locked because your email was not verified after multiple attempts. Please contact support or sign up again.",
		signOut: "Sign Out",
		support: "Contact Support",
	},
	status: {
		sending: "Sending…",
		sent: "Verification email sent! Check your inbox.",
		error: "Failed to send email. Please try again.",
	},
	sentTo: "Sent to",
} as const;

// ============================================================================
// Types
// ============================================================================

type ModalMode = "prompt" | "blocking" | "revoked" | "none";

// Max tokens before revocation
const MAX_SEND_COUNT = 3;

// ============================================================================
// Component
// ============================================================================

export function EmailVerificationModal() {
	const auth = useAuth();
	const pathname = usePathname();
	const { isLocked } = useSessionActivity();

	const [dismissed, setDismissed] = useState(false);
	const [isSending, setIsSending] = useState(false);
	const [sendResult, setSendResult] = useState<"sent" | "error" | null>(null);
	const [isFocusManagementReady, setIsFocusManagementReady] = useState(false);
	const dialogRef = useRef<HTMLDivElement>(null);
	const primaryActionRef = useRef<HTMLButtonElement>(null);
	const returnFocusRef = useRef<HTMLElement | null>(null);
	const wasOpenRef = useRef(false);
	const titleId = useId();
	const descriptionId = useId();

	// Determine modal mode
	const mode: ModalMode = useMemo(() => {
		if (!auth?.isAuthenticated || !auth.emailVerification) return "none";

		const { status, tokenExpiresAt, sentCount } = auth.emailVerification;

		// Already verified — nothing to show
		if (status === "verified") return "none";

		// Revoked — too many attempts
		if (status === "failed" || sentCount >= MAX_SEND_COUNT) {
			return "revoked";
		}

		// Token expired — blocking
		if (status === "expired") return "blocking";

		// Check if token has expired by time even if status hasn't been updated yet
		if (tokenExpiresAt) {
			const expiresAt = new Date(tokenExpiresAt).getTime();
			if (Date.now() > expiresAt) return "blocking";
		}

		// Awaiting verification — prompt (dismissible)
		if (status === "awaiting") return "prompt";

		return "none";
	}, [auth]);
	const isBlocking = mode === "blocking" || mode === "revoked";
	const isExcludedPath = pathname === "/" || pathname === "/verify-email";
	const isOpen = !isLocked
		&& !isExcludedPath
		&& mode !== "none"
		&& !(mode === "prompt" && dismissed);

	useEffect(() => {
		setIsFocusManagementReady(true);
	}, []);

	useLayoutEffect(() => {
		if (isOpen && !wasOpenRef.current) {
			returnFocusRef.current = document.activeElement instanceof HTMLElement
				&& document.activeElement !== document.body
				? document.activeElement
				: null;
		}

		wasOpenRef.current = isOpen;
	}, [isOpen]);

	const getReturnFocusElement = useCallback(() => {
		if (returnFocusRef.current?.isConnected) {
			return returnFocusRef.current;
		}

		return Array.from(document.querySelectorAll<HTMLElement>(
			'button:not([disabled]), a[href], input:not([disabled]), [tabindex="0"]',
		)).find((element) => !dialogRef.current?.contains(element)) ?? null;
	}, []);

	useModalFocusManagement({
		dialogRef,
		enabled: isOpen && isFocusManagementReady,
		getFallbackFocusElement: getReturnFocusElement,
		initialFocusRef: primaryActionRef,
		returnFocusElement: null,
	});

	// Reset dismissed when mode escalates
	useEffect(() => {
		if (mode === "blocking" || mode === "revoked") {
			setDismissed(false);
		}
	}, [mode]);

	// Clear send result after a delay
	useEffect(() => {
		if (sendResult === "sent") {
			const timer = setTimeout(() => setSendResult(null), 5000);
			return () => clearTimeout(timer);
		}
	}, [sendResult]);

	// Handle resend verification email
	const handleResend = useCallback(async () => {
		setIsSending(true);
		setSendResult(null);

		try {
			const res = await fetch("/api/auth/send-verification-email", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
			});

			if (res.ok) {
				setSendResult("sent");
			} else {
				setSendResult("error");
			}
		} catch {
			setSendResult("error");
		} finally {
			setIsSending(false);
		}
	}, []);

	// Handle sign out (revoked state)
	const handleSignOut = useCallback(() => {
		signOut({ callbackUrl: "/" });
	}, []);

	const handleKeyDown = useCallback((event: KeyboardEvent) => {
		if (event.key !== "Escape") return;

		event.preventDefault();
		event.stopPropagation();
		if (!isBlocking) setDismissed(true);
	}, [isBlocking]);

	if (!isOpen) return null;

	return (
		<AnimatePresence>
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				style={{
					position: "fixed",
					inset: 0,
					zIndex: 9999,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					backgroundColor: isBlocking
						? "rgba(0, 0, 0, 0.7)"
						: "rgba(0, 0, 0, 0.5)",
					backdropFilter: isBlocking ? "blur(4px)" : "blur(2px)",
					fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
				}}
				data-testid="email-verification-overlay"
				onClick={!isBlocking ? () => setDismissed(true) : undefined}
				onKeyDown={handleKeyDown}
			>
				<motion.div
					ref={dialogRef}
					aria-describedby={descriptionId}
					aria-labelledby={titleId}
					aria-modal="true"
					data-testid="email-verification-dialog"
					initial={{ scale: 0.95, opacity: 0, y: 10 }}
					animate={{ scale: 1, opacity: 1, y: 0 }}
					exit={{ scale: 0.95, opacity: 0, y: 10 }}
					role="dialog"
					tabIndex={-1}
					transition={{ type: "spring", damping: 25, stiffness: 300 }}
					onClick={(e) => e.stopPropagation()}
					style={{
						background: "#fff",
						borderRadius: 20,
						padding: "32px 28px",
						maxWidth: 400,
						width: "90%",
						boxShadow:
							"0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)",
						textAlign: "center",
					}}
				>
					{/* Icon */}
					<div
						style={{
							width: 64,
							height: 64,
							borderRadius: "50%",
							margin: "0 auto 20px",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							backgroundColor:
								mode === "revoked"
									? "#FEE2E2"
									: mode === "blocking"
										? "#FEF3C7"
										: "#DBEAFE",
						}}
					>
						{mode === "revoked" ? (
							<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
								<circle cx="12" cy="12" r="10" />
								<line x1="15" y1="9" x2="9" y2="15" />
								<line x1="9" y1="9" x2="15" y2="15" />
							</svg>
						) : mode === "blocking" ? (
							<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
								<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
								<line x1="12" y1="9" x2="12" y2="13" />
								<line x1="12" y1="17" x2="12.01" y2="17" />
							</svg>
						) : (
							<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#5BA9E1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
								<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
								<polyline points="22,6 12,13 2,6" />
							</svg>
						)}
					</div>

					{/* Title */}
					<h2
						id={titleId}
						style={{
							fontSize: 20,
							fontWeight: 700,
							color: "#111827",
							margin: "0 0 8px",
							lineHeight: 1.3,
						}}
					>
						{mode === "revoked"
							? STRINGS.revoked.title
							: mode === "blocking"
								? STRINGS.blocking.title
								: STRINGS.prompt.title}
					</h2>

					{/* Description */}
					<p
						id={descriptionId}
						style={{
							fontSize: 14,
							color: "#6B7280",
							margin: "0 0 24px",
							lineHeight: 1.6,
						}}
					>
						{mode === "revoked"
							? STRINGS.revoked.description
							: mode === "blocking"
								? STRINGS.blocking.description
								: STRINGS.prompt.description}
					</p>

					{/* User email */}
					{auth?.user?.email && mode !== "revoked" && (
						<div
							style={{
								fontSize: 13,
								color: "#9CA3AF",
								marginBottom: 20,
								padding: "8px 16px",
								backgroundColor: "#F9FAFB",
								borderRadius: 8,
								border: "1px solid #F3F4F6",
							}}
						>
							{STRINGS.sentTo} <strong style={{ color: "#374151" }}>{auth.user.email}</strong>
						</div>
					)}

					{/* Status message */}
					{sendResult && (
						<div
							aria-live="polite"
							role={sendResult === "error" ? "alert" : "status"}
							style={{
								fontSize: 13,
								padding: "10px 16px",
								borderRadius: 8,
								marginBottom: 16,
								backgroundColor:
									sendResult === "sent" ? "#ECFDF5" : "#FEF2F2",
								color:
									sendResult === "sent" ? "#065F46" : "#991B1B",
								border: `1px solid ${sendResult === "sent" ? "#A7F3D0" : "#FECACA"}`,
							}}
						>
							{sendResult === "sent"
								? STRINGS.status.sent
								: STRINGS.status.error}
						</div>
					)}

					{/* Actions */}
					<div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
						{mode === "revoked" ? (
							<>
								<button
									ref={primaryActionRef}
									type="button"
									onClick={handleSignOut}
									style={{
										width: "100%",
										padding: "12px 24px",
										borderRadius: 12,
										border: "none",
										backgroundColor: "#DC2626",
										color: "#fff",
										fontSize: 15,
										fontWeight: 600,
										cursor: "pointer",
										transition: "background-color 0.2s",
									}}
									onMouseEnter={(e) =>
										(e.currentTarget.style.backgroundColor = "#B91C1C")
									}
									onMouseLeave={(e) =>
										(e.currentTarget.style.backgroundColor = "#DC2626")
									}
								>
									{STRINGS.revoked.signOut}
								</button>
								<a
									href="mailto:support@vastipost.com"
									style={{
										fontSize: 13,
										color: "#6B7280",
										textDecoration: "underline",
										cursor: "pointer",
									}}
								>
									{STRINGS.revoked.support}
								</a>
							</>
						) : (
							<>
								{/* Resend button */}
								<button
									ref={primaryActionRef}
									type="button"
									onClick={handleResend}
									disabled={isSending}
									style={{
										width: "100%",
										padding: "12px 24px",
										borderRadius: 12,
										border: "none",
										backgroundColor: isSending ? "#93C5FD" : "#5BA9E1",
										color: "#fff",
										fontSize: 15,
										fontWeight: 600,
										cursor: isSending ? "not-allowed" : "pointer",
										transition: "background-color 0.2s",
										opacity: isSending ? 0.7 : 1,
									}}
									onMouseEnter={(e) => {
										if (!isSending)
											e.currentTarget.style.backgroundColor = "#4A98D0";
									}}
									onMouseLeave={(e) => {
										if (!isSending)
											e.currentTarget.style.backgroundColor = "#5BA9E1";
									}}
								>
									{isSending
										? STRINGS.status.sending
										: mode === "blocking"
											? STRINGS.blocking.resend
											: STRINGS.prompt.resend}
								</button>

								{/* Dismiss (prompt only) */}
								{mode === "prompt" && (
									<button
										type="button"
										onClick={() => setDismissed(true)}
										style={{
											width: "100%",
											padding: "10px 24px",
											borderRadius: 12,
											border: "1px solid #E5E7EB",
											backgroundColor: "transparent",
											color: "#6B7280",
											fontSize: 14,
											fontWeight: 500,
											cursor: "pointer",
											transition: "background-color 0.2s",
										}}
										onMouseEnter={(e) =>
											(e.currentTarget.style.backgroundColor = "#F9FAFB")
										}
										onMouseLeave={(e) =>
											(e.currentTarget.style.backgroundColor = "transparent")
										}
									>
										{STRINGS.prompt.dismiss}
									</button>
								)}

								{/* Spam hint (prompt only) */}
								{mode === "prompt" && (
									<p
										style={{
											fontSize: 12,
											color: "#9CA3AF",
											margin: "4px 0 0",
										}}
									>
										{STRINGS.prompt.checkSpam}
									</p>
								)}
							</>
						)}
					</div>
				</motion.div>
			</motion.div>
		</AnimatePresence>
	);
}

export default EmailVerificationModal;
